import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { api } from "../lib/api";

const CATEGORIES = [
  { key: "", label: "All" },
  { key: "backpacks", label: "Backpacks" },
  { key: "travel", label: "Travel" },
  { key: "hbcu", label: "HBCU" },
  { key: "tshirts", label: "T-Shirts" },
  { key: "embroidery", label: "Embroidery" },
  { key: "kids", label: "Books & Kids" },
  { key: "steam-kits", label: "STEAM Kits" },
];

const SORTS = [
  { key: "featured", label: "Featured" },
  { key: "price-asc", label: "Price: Low to High" },
  { key: "price-desc", label: "Price: High to Low" },
  { key: "name-asc", label: "Name: A-Z" },
];

const Shop = () => {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const category = params.get("category") || "";

  useEffect(() => {
    setLoading(true);
    const config = category ? { params: { category } } : {};
    api.get("/products", config)
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category]);

  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "price-asc") arr.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") arr.sort((a, b) => b.price - a.price);
    else if (sort === "name-asc") arr.sort((a, b) => a.name.localeCompare(b.name));
    else arr.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    return arr;
  }, [products, sort]);

  const currentCat = CATEGORIES.find((c) => c.key === category);

  return (
    <main data-testid="shop-page" className="bg-white">
      {/* Header */}
      <section className="container-x pt-12 pb-8">
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-3">The Shop</div>
        <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl text-black tracking-tight">
          {currentCat && currentCat.key ? (
            <>{currentCat.label}<span className="font-black">.</span></>
          ) : (
            <>All <span className="font-black">products.</span></>
          )}
        </h1>
        <p className="mt-3 text-[#4A4A4A] max-w-xl font-light">
          {products.length} pieces. Made with intention, printed with love.
        </p>
      </section>

      {/* Filter/Sort bar */}
      <section className="border-y border-[#E5E2DA] sticky top-[105px] z-30 bg-white/95 backdrop-blur">
        <div className="container-x py-4 flex items-center gap-4 flex-wrap">
          <div className="flex flex-wrap gap-1 flex-1">
            {CATEGORIES.map((c) => (
              <button
                key={c.key || "all"}
                data-testid={`filter-${c.key || "all"}`}
                onClick={() => setParams(c.key ? { category: c.key } : {})}
                className={`px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase font-semibold transition ${
                  category === c.key
                    ? "text-[#EC008C] border-b border-[#EC008C]"
                    : "text-black hover:text-[#EC008C]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="sort" className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#4A4A4A]">Sort:</label>
            <select
              id="sort"
              data-testid="shop-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="text-xs bg-transparent border border-[#E5E2DA] px-3 py-1.5 font-medium focus:outline-none focus:border-black"
            >
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="container-x py-12">
        {loading ? (
          <div className="text-center py-20 text-[#4A4A4A]">Loading products...</div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-20 text-[#4A4A4A]">No products found in this category.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
            {sorted.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default Shop;
